import keyBy from 'lodash/keyBy';
import uniqBy from 'lodash/uniqBy';
import { getPartnerPromotions as getPartnerPromotionsProxy } from '@atlassian/temp-proxies';
import { BillingFrequency, CycleInterval } from '@css-xp/graphtypes/src/generated-types-new';
import { getCorrectProgramLevelGlobal } from '@css-xp/ppc-utils';
import { SubscriptionRelationshipType } from '@css-xp/ppc-constants';
import { CartLinkedEntitlementsSchema, CartProductSchema, LinkedEntitlementRelationShipType } from '../../types';
import type { State, PartnerPromotionMap } from '../types';
export async function getPartnerPromotions({
  existingPromotions,
  existingPromotionBenefits,
  selectedProducts,
  selectedLinkedEntitlements = [],
  selectedPartnerAccountData,
  isExpandPromotionBenefits = false,
}: {
  existingPromotions: State['partnerPromotions'];
  existingPromotionBenefits: State['partnerPromotionBenefits'];
  selectedProducts: CartProductSchema[];
  selectedLinkedEntitlements?: CartLinkedEntitlementsSchema[];
  selectedPartnerAccountData: State['selectedPartnerAccountData'];
  isExpandPromotionBenefits?: boolean;
}): Promise<PartnerPromotionMap> {
  if (!selectedPartnerAccountData) {
    return {
      productIdPartnerPromotionIdMap: existingPromotions,
      promotionIdToBenefitsMap: existingPromotionBenefits,
    };
  }

  const filteredApps = selectedLinkedEntitlements?.filter((linkedEntitlement) => {
    return linkedEntitlement?.relationshipType === LinkedEntitlementRelationShipType?.APP;
  });

  const filteredAddOns = selectedLinkedEntitlements?.filter((linkedEntitlement) => {
    return linkedEntitlement?.relationshipType === LinkedEntitlementRelationShipType?.ADD_ON;
  });
  const appsIds = Object.keys(keyBy(filteredApps, 'id'));
  const addOnsIds = Object.keys(keyBy(filteredAddOns, 'id'));
  const { discountTier, programLevelGlobal } = selectedPartnerAccountData;
  const productsWithUnfetchedPromotionData = uniqBy(
    [...selectedProducts, ...selectedLinkedEntitlements],
    'productId'
  ).filter(({ productId }) => {
    return !existingPromotions[productId];
  });

  // promotions for all products have been fetched. return `existingPromotions`
  if (productsWithUnfetchedPromotionData.length === 0) {
    return {
      productIdPartnerPromotionIdMap: existingPromotions,
      promotionIdToBenefitsMap: existingPromotionBenefits,
    };
  }
  const actualProgramLevelGlobal = getCorrectProgramLevelGlobal(selectedPartnerAccountData);

  const partnerPromotionPromises = productsWithUnfetchedPromotionData.map((product) => {
    const isApp = appsIds.includes(product.id);
    const isAddOn = addOnsIds.includes(product.id);
    const relationshipType = (() => {
      if (isApp) return SubscriptionRelationshipType.APP_DEPENDENCE;
      if (isAddOn) return SubscriptionRelationshipType.ADDON_DEPENDENCE;
    })();

    return getPartnerPromotionsProxy(
      {
        productKeys: [product.productId],
        pricingType: 'PAID',
        // FIXME:: Handle the case when annual subscriptions will be supported
        // the billing cycle for the Free product is empty
        cycleInterval: CycleInterval.MONTH,
        partnerStatus: {
          discountTier,
          programLevelGlobal: actualProgramLevelGlobal,
        },
        ...(isApp || isAddOn
          ? {
              offeringRelationship: [
                {
                  direction: 'TO',
                  type: relationshipType,
                },
              ],
            }
          : {}),
      },
      isExpandPromotionBenefits
    );
  });

  const responses = await Promise.all(partnerPromotionPromises);

  const productIdPartnerPromotionIdMap: State['partnerPromotions'] = existingPromotions;
  const promotionIdToBenefitsMap: State['partnerPromotionBenefits'] = existingPromotionBenefits;

  responses.forEach((response, index) => {
    const availablePromotionIds = response?.availablePromotion?.['ACTIVE'];
    if (availablePromotionIds?.length && availablePromotionIds?.length > 1) {
      throw new Error('Multiple promotions are available.');
    }
    const product = productsWithUnfetchedPromotionData[index];

    productIdPartnerPromotionIdMap[product.productId] = availablePromotionIds?.[0];

    const promotionDefinition = response?.promotions?.[0];
    if (promotionDefinition) {
      promotionIdToBenefitsMap[promotionDefinition.promotionId] = promotionDefinition.benefits;
    }
  });

  return {
    productIdPartnerPromotionIdMap,
    promotionIdToBenefitsMap,
  };
}
